import * as React from "react";
import { toast } from "react-toastify";

import { FilterParams } from "src/library/utils";
import { ActionButton } from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { ButtonLink } from "src/library/components/Link";
import { Modal } from "src/library/components/Modal";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { usersTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import { PaginatedParams } from "src/core/shared/api";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "firstName", "ASC" ] ]
};

/**
 * Main "Users" View
 *
 * @returns
 */
const UsersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology, history } = props;
  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const [ usersResponse, getUsers ] = useUserApi("LIST_USERS");
  const usersData = usersResponse.data;
  const [ deleteUserResponse, deleteUser ] = useUserApi("DELETE_USER");
  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const checkPermission = useCheckUserPermission();

  const canCreateUsers = React.useMemo(() => {
    return checkPermission("create-user");
  }, [ checkPermission ]);

  const onFetchData = React.useCallback((filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>) => {
    getUsers({
      pagination,
      filters
    });
  }, [ getUsers ]);

  const CreateNewUserButton = (
    <ButtonLink
      background="primaryDark"
      to="/admin/users/create"
    >
      {`Create a new admin ${terminology.user.singular.text}`}
    </ButtonLink>
  );

  const editUserAction = (id: number) => {
    history.push(`/admin/users/${id}`);
  };

  const deleteUserAction = (id: number) => {
    setModalData(id);
  };

  const resendInviteUserAction = (id: number) => {
    resendInvite({ id });
  };

  const deleteUserActionConfirm = () => {
    if (modalData) {
      deleteUser({ id: modalData });
      setModalData(null);
    }
  };

  const handleSuspendUser = (user: any) => {
    const updatedUser = {
      ...user,
      status: user.status === "active" ? "suspended" : "active"
    };

    suspendUser(updatedUser);
  };

  // On user update
  React.useEffect(() => {
    if (suspendUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully updated.`);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (suspendUserResponse.error) {
      toast.error(`Could not update ${terminology.user.singular.text} - ${suspendUserResponse.error.message}`);
    }
  }, [ suspendUserResponse ]);

  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse ]);

  // On user deletion
  React.useEffect(() => {
    if (deleteUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully deleted.`);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (deleteUserResponse.error) {
      toast.error(`Could not delete ${terminology.user.singular.text} - ${deleteUserResponse.error.message}`);
    }
  }, [ deleteUserResponse ]);

  return (
    <Layout
      {...props}
      title={`Admin ${terminology.user.plural.text}`}
    >
      <MainContentWrapper
        fill
        Header={canCreateUsers && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={[ CreateNewUserButton ]}
          />
        )}
      >

        <Table
          tableId="global"
          ref={TableRef}
          error={usersResponse.error}
          loading={usersResponse.pending}
          tableOf={terminology.user.plural.text}
          data={usersData ? usersData.result : []}
          defaultFilters={{ customerId: null }}
          onFetchData={onFetchData}
          totalRows={usersData ? usersData.count : 0}
          pages={usersData ? usersData.pages : 0}
          defaultPagination={defaultPagination}
          mainAction={row => editUserAction(row.original.id)}
          columns={usersTableColumns({
            ActionButton: row => {
              const canAmendUser = checkPermission("amend-user");

              if (!canAmendUser) {
                return null;
              }

              return (
                <ActionButton
                  title="Actions"
                  items={[
                    {
                      title: "Edit",
                      action: () => editUserAction(row.original.id)
                    },
                    {
                      title: `${row.original.status === "active" ? "Suspend" : "Activate"}`,
                      action: () => {
                        handleSuspendUser(row.original);
                      }
                    },
                    {
                      title: "Resend invite",
                      action: () => resendInviteUserAction(row.original.id)
                    },
                    {
                      title: "Delete",
                      action: () => deleteUserAction(row.original.id)
                    }
                  ]}
                />
              );
            }
          })}
        />
      </MainContentWrapper>

      <Modal
        small
        show={modalData !== null}
        type="confirm"
        title="Are you sure?"
        body="Deleting this user cannot be undone."
        backgroundAction={() => setModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            action: deleteUserActionConfirm,
            $buttonType: "primary"
          }
        ]}
      />
    </Layout>
  );
};

export default UsersPage;
